import React, { useEffect, useMemo, useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import { useUIStore } from '@/store/useUIStore';
import { IspPage } from './ispLayout';
import { RowActions, actionColumn, ROW_ACTION_GAP } from './ispSettingsUI';
import {
   getIspWalledGardenState,
   applyIspWalledGarden,
   getIspServers,
   getIspServerModels,
   probeIspServer,
   type IspServerRow,
   type IspSetupStage,
} from '@/api/isp';

/**
 * ISP Business — Servers.
 *
 * The operator's MikroTiks running PPPoE. "Server", not "router": in Piso WiFi a
 * router is the thing being managed, and here it is the thing SERVING internet
 * to other people's routers — same word, opposite role, one dashboard.
 *
 * ─── THE SHAPE: two lists, and a button ───
 * Ray, 2026-08-29: *"the UI here should be similar in adding a vendo… wizard
 * steps, wait pending table listing and done listing."* The numbered steps live
 * in the Add wizard, where the operator is actually walking them — a rail of
 * step cards was tried on this page and Ray cut it: *"remove the adding a
 * mikrotik 3 cards UI, just a button is fine."* A page that lists servers should
 * list servers.
 *
 * So: the MikroTiks still WAITING for their paste, then the ones that came up.
 * The two lists are separate on purpose and were briefly merged into one table
 * by a different session — that merge is undone here, because the rows are not
 * the same kind of thing:
 *
 *   Waiting  → an unfinished JOB. Someone must walk to a router and paste.
 *   Connected → a running server. Only interesting when a number looks wrong.
 *
 * One table sorted by status makes the operator read the whole fleet to find the
 * two rows asking them for something.
 *
 * ─── ONLINE COMES FROM THE TUNNEL ───
 * Not from a flag we set when the script was handed over. A peer with a
 * handshake in the last few minutes is a router that is really there; anything
 * softer would show green for a MikroTik unplugged a week ago.
 *
 * ─── 🔴 "TUNNEL UP" IS NOT "SET UP" ───
 * The paste is split in two because Winbox silently truncates a long one. A
 * router that got part 1 and lost part 2 has a live tunnel and answers nothing.
 * It stays in the WAITING list, saying which part is missing — showing it as a
 * connected server that happens to report no clients is exactly the hidden
 * half-configured state the split exists to prevent.
 *
 * ─── TWO SOURCES OF TRUTH, KEPT APART ───
 *   Clients (online / on router)   → the MikroTik: /ppp/active, /ppp/secret
 *   CPU · Memory · Storage · Up    → the MikroTik: /system/resource
 *   PPPoE service                  → the MikroTik: /interface/pppoe-server/server
 *   Subscribers · Suspended · Cut off → SmartFi's OWN IspAccount records
 *
 * The last row is a BILLING state. A MikroTik has no idea an account is
 * suspended for non-payment — it only sees a secret on a walled-garden profile —
 * so counting suspensions off the router would produce a number that agrees with
 * the box and disagrees with the invoice. Those columns read zero until the
 * Accounts page exists, and the page says so in words rather than borrowing the
 * router's numbers to look busy.
 */

type ServerSort = 'name' | 'clients' | 'load' | 'seen';
type ViewMode = 'card' | 'list';

/** Waiting for a paste is a JOB; a connected server is a thing to watch. */
const isWaiting = (s: IspServerRow) =>
   s.setupStage === 'waiting' || s.setupStage === 'tunnel-only';

const STAGE_LABEL: Record<IspSetupStage, string> = {
   waiting: 'Not pasted yet',
   'tunnel-only': 'Part 2 missing',
   ready: 'Online',
   offline: 'Offline',
};

/** What the operator has left to do on a row that is not finished. */
const whatsLeft = (s: IspServerRow): string =>
   s.setupStage === 'tunnel-only'
      ? 'Tunnel is up. Paste part 2 — Winbox drops the end of a long paste without saying so.'
      : 'Paste part 1, then part 2, into Winbox → New Terminal.';

/** Seconds since the last handshake, in the words an operator would use. */
const agoLabel = (sec: number | null): string => {
   if (sec === null) return '—';
   if (sec < 10) return 'just now';
   if (sec < 60) return `${sec}s ago`;
   if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
   if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
   return `${Math.floor(sec / 86400)}d ago`;
};

/**
 * How long the router has been up.
 *
 * "Just restarted" under two minutes, deliberately: a PPPoE server that keeps
 * bouncing is exactly why somebody opens this page, and a tidy "0d 0h" buries
 * the one reading that would have told them.
 */
const uptimeLabel = (sec: number | null): string => {
   if (sec === null) return '—';
   if (sec < 120) return 'just restarted';
   const d = Math.floor(sec / 86400);
   const h = Math.floor((sec % 86400) / 3600);
   const m = Math.floor((sec % 3600) / 60);
   if (d > 0) return `${d}d ${h}h`;
   if (h > 0) return `${h}h ${m}m`;
   return `${m}m`;
};

/** GB/MB, enough to tell a 16MB flash from a 128MB one. */
const bytesLabel = (b: number | null): string => {
   if (b === null || b <= 0) return '';
   if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(1)} GB`;
   if (b >= 1024 ** 2) return `${Math.round(b / 1024 ** 2)} MB`;
   return `${Math.round(b / 1024)} KB`;
};

/**
 * Amber past 75%, red past 90%.
 *
 * Applied to CPU, memory and storage alike. A CCR at 92% memory and a hEX with
 * 92% of its flash gone are both a call to make before something falls over.
 */
const loadTone = (pct: number | null): 'ok' | 'warn' | 'bad' =>
   pct === null ? 'ok' : pct >= 90 ? 'bad' : pct >= 75 ? 'warn' : 'ok';

const IspServers: React.FC = () => {
   const qc = useQueryClient();
   const { handleShow, handleClose } = useUIStore();

   const [sortBy, setSortBy] = useState<ServerSort>('name');
   const [viewMode, setViewMode] = useState<ViewMode>('list');
   const [refreshing, setRefreshing] = useState(false);

   const { data, isLoading, isError } = useQuery({
      queryKey: ['isp-servers'],
      queryFn: getIspServers,
      // Somebody standing at a router waiting for it to come up should not have
      // to reload the page to find out that it has.
      refetchInterval: 10_000,
   });

   // Same queryKey the Add form uses, so this costs nothing once either has run.
   const { data: models } = useQuery({
      queryKey: ['isp-server-models'],
      queryFn: getIspServerModels,
      staleTime: 60 * 60 * 1000,
   });

   const modelLabel = (id: string) =>
      models?.find((m) => m.id === id)?.label || id || '—';

   // Ungated on purpose. Registering a router is the FIRST thing an operator
   // can usefully do, and the business record is created on demand behind it.
   const openAdd = () =>
      handleShow({
         template: 'isp-server-add',
         title: 'Add a MikroTik',
         windowType: 'desktop',
         data: {
            onCancel: handleClose,
            onAdded: () => {
               qc.invalidateQueries({ queryKey: ['isp-servers'] });
               qc.invalidateQueries({ queryKey: ['isp-readiness'] });
            },
         },
      });

   const openScript = (s: IspServerRow) =>
      handleShow({
         template: 'isp-server-add',
         title: `Setup — ${s.name}`,
         windowType: 'desktop',
         data: {
            onCancel: handleClose,
            onAdded: () => qc.invalidateQueries({ queryKey: ['isp-servers'] }),
            showScriptFor: { id: s.id, name: s.name },
         },
      });

   /**
    * Removing a server is a modal, never a native confirm — this dashboard does
    * not use browser dialogs, and a one-line "Are you sure?" could not carry
    * what the operator has to be told: the tunnel, account and firewall rule
    * SmartFi added stay on their router until they clear them out.
    */
   /** Rename, or change the VLAN subscribers arrive on (Ray, 2026-10-30). */
   const openEdit = (s: IspServerRow) =>
      handleShow({
         template: 'isp-server-edit',
         title: `Edit ${s.name}`,
         windowType: 'desktop',
         data: {
            server: {
               id: s.id,
               name: s.name,
               subscriberVlan: s.subscriberVlan ?? 0,
               smartfiBuilt: s.smartfiBuilt,
            },
            onCancel: handleClose,
            onSaved: () => {
               handleClose();
               qc.invalidateQueries({ queryKey: ['isp-servers'] });
            },
         },
      });

   const openDelete = (s: IspServerRow) =>
      handleShow({
         template: 'isp-server-delete',
         title: `Remove ${s.name}`,
         windowType: 'desktop',
         data: {
            server: { id: s.id, name: s.name },
            onCancel: handleClose,
            onDeleted: () => {
               handleClose();
               qc.invalidateQueries({ queryKey: ['isp-servers'] });
            },
         },
      });

   const rows = useMemo(() => data ?? [], [data]);

   /**
    * Which routers already have the walled garden on.
    *
    * Read off each MikroTik rather than remembered here: an operator may have
    * pasted the old script by hand, or a rule may have been removed since, and
    * a button that lies about the state is worse than one that says nothing.
    */
   const [walledOn, setWalledOn] = useState<Record<string, boolean>>({});
   useEffect(() => {
      let stop = false;
      Promise.all(
         rows
            .filter((s) => s.setupStage === 'ready')
            .map((s) =>
               getIspWalledGardenState(s.id)
                  .then((w) => [s.id, w.profile && w.wall && w.redirect] as const)
                  // Unreachable is not "not set up". Leaving it unknown beats
                  // telling somebody to configure a router that already is.
                  .catch(() => [s.id, false] as const)
            )
      ).then((pairs) => {
         if (!stop) setWalledOn(Object.fromEntries(pairs));
      });
      return () => {
         stop = true;
      };
   }, [rows]);

   /** Routers a safety check stopped us building on, each with its reason. */
   const blocked = rows.filter((s) => !!s.setupBlockedBy);

   /** Connected routers where Suspend still does nothing. */
   /** Connected routers that serve no PPPoE — nobody can dial in. */
   const noPppoe = rows.filter(
      (s) => s.setupStage === 'ready' && !!s.pppoeWarning
   );

   const needsWalling = rows.filter(
      (s) => s.setupStage === 'ready' && walledOn[s.id] === false
   );

   const walling = useMutation({
      mutationFn: (id: string) => applyIspWalledGarden(id),
      onSuccess: (r: { message: string }) => {
         qc.invalidateQueries({ queryKey: ['isp-servers'] });
         handleClose();
         toast.success(r.message);
      },
      onError: (e: unknown) =>
         toast.error(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not set it up.'
         ),
   });

   /**
    * ⚠️ ASKED FIRST, because it changes what happens to LIVE customers.
    *
    * Anyone already marked Suspended is still browsing today; the moment this
    * lands they stop. That has to be said before it happens, not after.
    */
   const confirmWalled = (s: IspServerRow) =>
      handleShow({
         template: 'isp-confirm',
         title: '',
         windowType: 'desktop',
         data: {
            title: walledOn[s.id]
               ? `Check cut-off on ${s.name}?`
               : `Set up cut-off on ${s.name}?`,
            message: walledOn[s.id]
               ? 'This is already set up. Running it again re-checks every rule and changes nothing that is already right.'
               : 'SmartFi will add a suspended profile and three firewall rules to this MikroTik. Anyone already marked Suspended stops browsing the next time they reconnect — until now they were not cut off at all. They will still be able to reach GCash and their bill page.',
            confirmLabel: walledOn[s.id] ? 'Re-check' : 'Set it up',
            onConfirm: () => walling.mutateAsync(s.id),
            onCancel: handleClose,
         },
      });


   /** The two lists. Waiting first — it is the one asking for something. */
   const waiting = useMemo(() => rows.filter(isWaiting), [rows]);
   const connected = useMemo(() => rows.filter((s) => !isWaiting(s)), [rows]);

   /**
    * Read every reachable router NOW.
    *
    * The list refreshes readings in the background as they go stale, which is
    * right for a page left open and wrong for an operator who has just pasted
    * part 2 and is standing there. Only reachable ones are asked: a router whose
    * tunnel is down would cost a six-second timeout to learn nothing.
    */
   const refreshNow = async () => {
      const targets = rows.filter((s) => s.online);
      if (!targets.length || refreshing) return;
      setRefreshing(true);
      try {
         await Promise.allSettled(targets.map((s) => probeIspServer(s.id)));
         await qc.invalidateQueries({ queryKey: ['isp-servers'] });
      } finally {
         setRefreshing(false);
      }
   };

   const counts = useMemo(() => {
      const c = { all: rows.length, registered: rows.length, tunnel: 0, ready: 0, offline: 0, half: 0 };
      rows.forEach((s) => {
         if (s.online) c.tunnel++;
         if (s.setupStage === 'ready') c.ready++;
         if (s.setupStage === 'offline') c.offline++;
         if (s.setupStage === 'tunnel-only') c.half++;
      });
      return c;
   }, [rows]);

   /**
    * The two headline totals, each summed only over the rows that really
    * reported one.
    *
    * `null` means nothing has been measured at all, and it renders as an em
    * dash. Summing nulls as zero would put a confident "0 clients online" on a
    * page where the true answer is that nobody has managed to ask yet.
    */
   const clientTotals = useMemo(() => {
      let online: number | null = null;
      let onRouter: number | null = null;
      rows.forEach((s) => {
         if (s.health?.onlineClients !== null && s.health?.onlineClients !== undefined) {
            online = (online ?? 0) + s.health.onlineClients;
         }
         if (
            s.health?.routerSecretCount !== null &&
            s.health?.routerSecretCount !== undefined
         ) {
            onRouter = (onRouter ?? 0) + s.health.routerSecretCount;
         }
      });
      return { online, onRouter };
   }, [rows]);

   const accountTotals = useMemo(
      () =>
         rows.reduce(
            (a, s) => ({
               total: a.total + (s.accounts?.total ?? 0),
               suspended: a.suspended + (s.accounts?.suspended ?? 0),
               disconnected: a.disconnected + (s.accounts?.disconnected ?? 0),
            }),
            { total: 0, suspended: 0, disconnected: 0 }
         ),
      [rows]
   );

   /**
    * Routers that are past setup and still will not answer.
    *
    * Deliberately excludes the half-pasted ones: those are an unfinished job and
    * belong in the waiting list with their own instruction, not in a fault note.
    */
   const unreadable = useMemo(
      () =>
         connected.filter((s) => s.online && s.health?.lastProbeError),
      [connected]
   );

   /**
    * The routers are carrying subscribers SmartFi has no record of.
    *
    * True for every operator before the Accounts page exists, and the honest
    * explanation for why "Subscribers" reads 0 next to a router with 200 secrets
    * on it. Without this sentence the zero looks like a bug.
    */
   const showAccountsGap =
      accountTotals.total === 0 && (clientTotals.onRouter ?? 0) > 0;

   const shown = useMemo(() => {
      // An unmeasured row sorts LAST under either numeric sort, never as a zero:
      // "we could not read it" is not "it is idle".
      const desc = (v: number | null | undefined) =>
         v === null || v === undefined ? -1 : v;
      return [...connected].sort((a, b) => {
         if (sortBy === 'clients')
            return (
               desc(b.health?.onlineClients) - desc(a.health?.onlineClients) ||
               a.name.localeCompare(b.name)
            );
         if (sortBy === 'load')
            return (
               desc(b.health?.cpuLoadPct) - desc(a.health?.cpuLoadPct) ||
               a.name.localeCompare(b.name)
            );
         if (sortBy === 'seen') {
            const av = a.handshakeAgoSec ?? Number.MAX_SAFE_INTEGER;
            const bv = b.handshakeAgoSec ?? Number.MAX_SAFE_INTEGER;
            return av - bv;
         }
         return a.name.localeCompare(b.name);
      });
   }, [connected, sortBy]);

   /*
    * 🔴 NO "PPPoE service" COLUMN. Ray, 2026-09-03: *"why PPPOE service column
    * is empty value? whats this? maybe this is not necessary?"* It showed
    * RouterOS's `name` for the service, which the router usually leaves blank
    * — the service is identified by `service-name` — so the column was empty
    * on a perfectly healthy router. And an operator has no use for either
    * label. The fact that DOES matter, a router serving no PPPoE at all, is
    * already said in words by `pppoeWarning`.
    */

   /** Tapping the id copies it — it exists to be pasted somewhere else. */
   const [copiedId, setCopiedId] = useState('');
   /*
    * 🔴 The READABLE name, not the ObjectId. Ray, 2026-09-03: *"i see mongodb
    * record id, i like something simple like the vendo ABC-12345678."* Twenty-
    * four hex characters cannot be read down a phone, and naming one router
    * out of a fleet during an outage is exactly when somebody is trying to.
    */
   const copyId = async (id: string) => {
      const row = rows.find((r) => r.id === id);
      try {
         await navigator.clipboard.writeText(row?.deviceCode || id);
         setCopiedId(id);
         window.setTimeout(() => setCopiedId(''), 1400);
      } catch {
         // No clipboard: it is on screen and selectable, which is the point.
      }
   };

   const radiusNote =
      'This router authenticates against RADIUS, so its PPPoE accounts are not stored on it. SmartFi cannot manage them yet.';

   /** A percentage bar, or an em dash when nothing was measured. */
   const Load: React.FC<{ pct: number | null; total?: number | null }> = ({
      pct,
      total,
   }) => {
      if (pct === null || pct === undefined) return <Dash>—</Dash>;
      const size = bytesLabel(total ?? null);
      return (
         <CellStack title={size ? `${pct}% of ${size}` : `${pct}%`}>
            <CellMain>{pct}%</CellMain>
            <Meter>
               <MeterFill $pct={pct} $tone={loadTone(pct)} />
            </Meter>
            {size && <CellSub>{size}</CellSub>}
         </CellStack>
      );
   };

   /**
    * Clients, from the ROUTER.
    *
    * Two numbers on purpose. "Online" is PPPoE sessions up right now; "on
    * router" is every secret configured on it, which is the only real total that
    * exists before an operator has moved their subscribers into SmartFi.
    */
   const Clients: React.FC<{ s: IspServerRow }> = ({ s }) => {
      const on = s.health?.onlineClients;
      const total = s.health?.routerSecretCount;
      if (on === null || on === undefined) return <Dash>—</Dash>;
      return (
         <CellStack>
            <CellMain $tone="ok">{on}</CellMain>
            <CellSub>
               {total === null || total === undefined
                  ? 'online'
                  : `of ${total} on router`}
            </CellSub>
         </CellStack>
      );
   };

   /**
    * Subscribers, from SMARTFI.
    *
    * Deliberately a separate cell from Clients, never merged with it: one is
    * what the MikroTik is carrying, the other is what SmartFi is billing, and an
    * operator mid-migration needs to see the gap rather than one blended number.
    */
   const Accounts: React.FC<{ s: IspServerRow }> = ({ s }) => {
      const a = s.accounts;
      if (!a || a.total === 0) return <Dash>none yet</Dash>;
      const cut = [
         a.suspended ? `${a.suspended} suspended` : '',
         a.disconnected ? `${a.disconnected} cut off` : '',
      ]
         .filter(Boolean)
         .join(' · ');
      return (
         <CellStack>
            <CellMain>{a.total}</CellMain>
            <CellSub $tone={cut ? 'warn' : undefined}>
               {cut || `${a.active} active`}
            </CellSub>
         </CellStack>
      );
   };

   return (
      <IspPage>
         <HeaderRow>
            <PageTitle>Servers</PageTitle>
            <ActionBtn type="button" onClick={openAdd}>
               + Add MikroTik
            </ActionBtn>
         </HeaderRow>

         <Banner>
            <BannerIcon>📡</BannerIcon>
            <BannerText>
               <BannerLabel>MikroTik PPPoE Servers</BannerLabel>
               <BannerHeadline>
                  <strong>{counts.all}</strong>{' '}
                  {counts.all === 1 ? 'server' : 'servers'}
                  <Sep>·</Sep>
                  <OnlineNum>{counts.ready} reporting</OnlineNum>
               </BannerHeadline>
               <BannerSub>
                  SmartFi reaches them over a tunnel — it never needs a port open
                  to the internet.
               </BannerSub>
            </BannerText>
            <BannerStats>
               <Stat title="Both parts pasted and answering SmartFi.">
                  <StatNum $tone="ok">{counts.ready}</StatNum>
                  <StatLbl>Reporting</StatLbl>
               </Stat>
               <Stat title="Set up once and now unreachable over the tunnel.">
                  <StatNum $tone={counts.offline ? 'bad' : 'plain'}>
                     {counts.offline}
                  </StatNum>
                  <StatLbl>Offline</StatLbl>
               </Stat>
               <Stat title="Still waiting for one or both pastes.">
                  <StatNum $tone={waiting.length ? 'warn' : 'plain'}>
                     {waiting.length}
                  </StatNum>
                  <StatLbl>Needs setup</StatLbl>
               </Stat>
               {/* 🔴 THESE TWO ARE DIFFERENT THINGS AND THE LABELS MUST SAY
                   SO. Ray asked outright, 2026-09-03: *"whats the difference
                   of client vs subscribers?"* One is measured on the router
                   this second; the other is who we bill, connected or not. A
                   tooltip was not enough — nobody hovers a number they have
                   already misread. */}
               <Stat title="PPPoE sessions up on the routers this second.">
                  <StatNum $tone="ok">
                     {clientTotals.online === null ? '—' : clientTotals.online}
                  </StatNum>
                  <StatLbl>Connected now</StatLbl>
               </Stat>
               <Stat title="People you bill, connected or not. SmartFi's own records.">
                  <StatNum $tone="plain">{accountTotals.total}</StatNum>
                  <StatLbl>People you bill</StatLbl>
               </Stat>
               <Stat title="Suspended or cut off in SmartFi — a billing state, not something the router reports.">
                  <StatNum
                     $tone={
                        accountTotals.suspended + accountTotals.disconnected
                           ? 'warn'
                           : 'plain'
                     }
                  >
                     {accountTotals.suspended + accountTotals.disconnected}
                  </StatNum>
                  <StatLbl>Suspended / cut off</StatLbl>
               </Stat>
            </BannerStats>
         </Banner>

         {isLoading && <SkeletonLines lines={3} />}
         {isError && <EmptyNote>Could not load your servers.</EmptyNote>}

         {!isLoading && !isError && rows.length === 0 && (
            <EmptyState>
               <EmptyBig>No MikroTiks yet</EmptyBig>
               <EmptySub>
                  Add one and paste the two short scripts it gives you into New
                  Terminal. They change nothing your subscribers are using — they
                  only open a tunnel home and let SmartFi read the router.
               </EmptySub>
               <ActionBtn type="button" onClick={openAdd}>
                  + Add MikroTik
               </ActionBtn>
            </EmptyState>
         )}

         {/* ─── List 1 · still waiting for a paste ─── */}
         {waiting.length > 0 && (
            <>
               <SectionRow>
                  <SectionHeader>
                     Waiting for setup
                     <CountChip $tone="warn">{waiting.length}</CountChip>
                  </SectionHeader>
                  <MutedHint>
                     These are asking you to go and paste something.
                  </MutedHint>
               </SectionRow>
               <TableWrapper>
                  <StyledTable>
                     <thead>
                        <tr>
                           <Th>Name</Th>
                           <Th>Model</Th>
                           <Th style={{ minWidth: 140 }}>Where it got to</Th>
                           <Th>What is left</Th>
                           <Th>Tunnel IP</Th>
                           <Th>Actions</Th>
                        </tr>
                     </thead>
                     <tbody>
                        {waiting.map((s) => (
                           <tr key={s.id}>
                              <Td $bold>{s.name}</Td>
                              <Td $muted>{modelLabel(s.model)}</Td>
                              <Td>
                                 <StatePill
                                    $state={
                                       s.setupStage === 'tunnel-only'
                                          ? 'half'
                                          : 'waiting'
                                    }
                                 >
                                    <Dot
                                       $state={
                                          s.setupStage === 'tunnel-only'
                                             ? 'half'
                                             : 'waiting'
                                       }
                                    />
                                    {STAGE_LABEL[s.setupStage]}
                                 </StatePill>
                              </Td>
                              <Td $muted style={{ whiteSpace: 'normal', maxWidth: 380 }}>
                                 {whatsLeft(s)}
                              </Td>
                              <Td $mono>{s.vpnIp || '—'}</Td>
                              <Td>
                                 <RowActions>
                                    <LinkBtn
                                       type="button"
                                       onClick={() => openScript(s)}
                                    >
                                       {s.setupStage === 'tunnel-only'
                                          ? 'Get part 2'
                                          : 'Continue setup'}
                                    </LinkBtn>
                                    <DangerBtn
                                       type="button"
                                       onClick={() => openDelete(s)}
                                    >
                                       Delete
                                    </DangerBtn>
                                 </RowActions>
                              </Td>
                           </tr>
                        ))}
                     </tbody>
                  </StyledTable>
               </TableWrapper>
            </>
         )}

         {/* ─── List 2 · the ones that came up ─── */}
         {connected.length > 0 && (
            <>
               <SectionRow>
                  <SectionHeader>
                     {/* 🔴 SAY WHY WE BUILT NOTHING. SmartFi refuses to touch a
                         MikroTik that already carries somebody's own network,
                         which is right — but it used to refuse into a server
                         log. The operator finished the wizard, saw no PPPoE
                         service and no message, and had no idea a safety check
                         had stopped it. The common case trips it too: a reset
                         WITH default configuration leaves ether2-5 bridged, so
                         the reset most operators do is not "bare". */}
                     {blocked.map((s) => (
                        <BlockedNotice key={s.id}>
                           <WallText>
                              <strong>
                                 SmartFi changed nothing on {s.name}
                              </strong>
                              <br />
                              {s.setupBlockedBy}
                           </WallText>
                        </BlockedNotice>
                     ))}

                     {noPppoe.length > 0 && (
                        /* 🔴 The failure that says nothing for itself. Plans
                           push fine, the login authenticates, and no address
                           ever comes back — Ray hit exactly this on his own
                           bench. A router SmartFi built is silent here. */
                        <WallNotice>
                           <WallText>
                              <strong>
                                 No PPPoE server on{' '}
                                 {noPppoe.length === 1
                                    ? noPppoe[0].name
                                    : `${noPppoe.length} MikroTiks`}
                              </strong>
                              <br />
                              {noPppoe[0].pppoeWarning}
                           </WallText>
                        </WallNotice>
                     )}

                     {needsWalling.length > 0 && (
                        /* Informational, not actionable. SmartFi applies this
                           itself on the next reading; an operator seeing it
                           for more than a minute or two has a router that
                           cannot be reached, which the Status column already
                           tells them about. */
                        <WallNotice>
                           <WallText>
                              <strong>Finishing setup…</strong>
                              <br />
                              SmartFi is configuring{' '}
                              {needsWalling.length === 1
                                 ? needsWalling[0].name
                                 : `${needsWalling.length} MikroTiks`}{' '}
                              so suspended customers are actually cut off and
                              see their bill. Nothing to press.
                           </WallText>
                        </WallNotice>
                     )}

                     Connected
                     <CountChip $tone="ok">{connected.length}</CountChip>
                  </SectionHeader>
                  <HeaderActions>
                     <SortSelect
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as ServerSort)}
                        title="Sort servers"
                     >
                        <option value="name">Name (A–Z)</option>
                        <option value="clients">Most clients</option>
                        <option value="load">Busiest (CPU)</option>
                        <option value="seen">Last seen</option>
                     </SortSelect>
                     <ViewToggle>
                        <ToggleBtn
                           type="button"
                           $active={viewMode === 'card'}
                           onClick={() => setViewMode('card')}
                        >
                           Cards
                        </ToggleBtn>
                        <ToggleBtn
                           type="button"
                           $active={viewMode === 'list'}
                           onClick={() => setViewMode('list')}
                        >
                           List
                        </ToggleBtn>
                     </ViewToggle>
                     <GhostBtn
                        type="button"
                        onClick={refreshNow}
                        disabled={refreshing || counts.tunnel === 0}
                        title={
                           counts.tunnel === 0
                              ? 'No router is reachable to read.'
                              : 'Read CPU, memory and client counts from the routers now.'
                        }
                     >
                        {refreshing ? 'Reading…' : 'Refresh readings'}
                     </GhostBtn>
                  </HeaderActions>
               </SectionRow>

               {viewMode === 'list' && (
                  <TableWrapper>
                     <StyledTable>
                        <thead>
                           <tr>
                              <Th>Name</Th>
                              <Th style={{ minWidth: 110 }}>Status</Th>
                              <Th title="PPPoE sessions up right now, read off the router.">
                                 Clients
                              </Th>
                              <Th title="Subscriber records in SmartFi — a billing count, not the router's.">
                                 Subscribers
                              </Th>
                              <Th>CPU</Th>
                              <Th>Memory</Th>
                              <Th>Storage</Th>
                              <Th>Uptime</Th>
                              <Th>Router</Th>
                              <Th>Tunnel IP</Th>
                              <Th>Last seen</Th>
                              <Th>Actions</Th>
                           </tr>
                        </thead>
                        <tbody>
                           {shown.map((s) => {
                              const st = s.online ? 'online' : 'offline';
                              return (
                                 <tr key={s.id}>
                                    <Td $bold>
                                       {s.name}
                                       {s.usesRadius && (
                                          <RadiusChip title={radiusNote}>
                                             RADIUS
                                          </RadiusChip>
                                       )}
                                       {s.online && s.health?.lastProbeError && (
                                          <ErrChip title={s.health.lastProbeError}>
                                             Can’t read
                                          </ErrChip>
                                       )}
                                       {/* 🔴 THE ID, WHERE THE NAME IS. Ray,
                                           2026-09-03: *"we should show the
                                           device id of each server registered
                                           for easy targeting remotely when
                                           something will happen."* Two
                                           MikroTiks can share a name; this
                                           never does, and it is what every log
                                           line and the router's own road-2 URL
                                           are keyed by. Tap to copy. */}
                                       <IdChip
                                          type="button"
                                          title="Copy this MikroTik's id"
                                          onClick={() => copyId(s.id)}
                                       >
                                          {copiedId === s.id
                                             ? 'copied'
                                             : s.deviceCode || s.id}
                                       </IdChip>
                                    </Td>
                                    <Td>
                                       <StatePill $state={st}>
                                          <Dot $state={st} />
                                          {STAGE_LABEL[s.setupStage]}
                                       </StatePill>
                                    </Td>
                                    <Td>
                                       <Clients s={s} />
                                    </Td>
                                    <Td>
                                       <Accounts s={s} />
                                    </Td>
                                    <Td>
                                       <Load pct={s.health?.cpuLoadPct ?? null} />
                                    </Td>
                                    <Td>
                                       <Load
                                          pct={s.health?.memoryUsedPct ?? null}
                                          total={s.health?.memoryTotalBytes ?? null}
                                       />
                                    </Td>
                                    <Td>
                                       <Load
                                          pct={s.health?.storageUsedPct ?? null}
                                          total={s.health?.storageTotalBytes ?? null}
                                       />
                                    </Td>
                                    <Td $muted>
                                       {uptimeLabel(s.health?.uptimeSec ?? null)}
                                    </Td>
                                    <Td>
                                       <CellStack>
                                          <CellMain>{modelLabel(s.model)}</CellMain>
                                          <CellSub>
                                             {s.routerOsVersion
                                                ? `RouterOS ${s.routerOsVersion}`
                                                : '—'}
                                             {s.health?.boardName
                                                ? ` · ${s.health.boardName}`
                                                : ''}
                                          </CellSub>
                                       </CellStack>
                                    </Td>
                                    <Td $mono>{s.vpnIp || '—'}</Td>
                                    <Td $muted>{agoLabel(s.handshakeAgoSec)}</Td>
                                    <Td>
                                       <RowActions>
                                          {/* 🔴 NOT A BUTTON. Ray, 2026-09-02:
                                              *"that cut off ready is not make
                                              sense… thats another unnotice
                                              action by an operator."* Right —
                                              it is the last step of connecting
                                              a router, not a decision, so
                                              SmartFi does it itself the moment
                                              it can read the box. This just
                                              reports whether it took. */}
                                          {walledOn[s.id] === false ? (
                                             <PendingChip title="SmartFi retries this automatically">
                                                Cut-off pending
                                             </PendingChip>
                                          ) : null}
                                          <LinkBtn
                                             type="button"
                                             onClick={() => openEdit(s)}
                                          >
                                             Edit
                                          </LinkBtn>
                                          <DangerBtn
                                             type="button"
                                             onClick={() => openDelete(s)}
                                          >
                                             Delete
                                          </DangerBtn>
                                       </RowActions>
                                    </Td>
                                 </tr>
                              );
                           })}
                        </tbody>
                     </StyledTable>
                  </TableWrapper>
               )}

               {viewMode === 'card' && (
                  <CardGrid>
                     {shown.map((s) => {
                        const st = s.online ? 'online' : 'offline';
                        return (
                           <ServerCard key={s.id} $state={st}>
                              <CardTop>
                                 <CardName>{s.name}</CardName>
                                 {/* Same id as the list view — see IdChip. */}
                                 <IdChip
                                    type="button"
                                    title="Copy this MikroTik's id"
                                    onClick={() => copyId(s.id)}
                                 >
                                    {copiedId === s.id
                                       ? 'copied'
                                       : s.deviceCode || s.id}
                                 </IdChip>
                                 <StatePill $state={st}>
                                    <Dot $state={st} />
                                    {STAGE_LABEL[s.setupStage]}
                                 </StatePill>
                              </CardTop>
                              <CardModel>
                                 {modelLabel(s.model)}
                                 {s.routerOsVersion
                                    ? ` · RouterOS ${s.routerOsVersion}`
                                    : ''}
                              </CardModel>
                              {s.usesRadius && <Warn>{radiusNote}</Warn>}
                              {s.online && s.health?.lastProbeError && (
                                 <Warn>{s.health.lastProbeError}</Warn>
                              )}
                              <CardMetrics>
                                 <Metric>
                                    <MetricLbl>Clients</MetricLbl>
                                    <Clients s={s} />
                                 </Metric>
                                 <Metric>
                                    <MetricLbl>Subscribers</MetricLbl>
                                    <Accounts s={s} />
                                 </Metric>
                                 <Metric>
                                    <MetricLbl>CPU</MetricLbl>
                                    <Load pct={s.health?.cpuLoadPct ?? null} />
                                 </Metric>
                                 <Metric>
                                    <MetricLbl>Memory</MetricLbl>
                                    <Load
                                       pct={s.health?.memoryUsedPct ?? null}
                                       total={s.health?.memoryTotalBytes ?? null}
                                    />
                                 </Metric>
                                 <Metric>
                                    <MetricLbl>Storage</MetricLbl>
                                    <Load
                                       pct={s.health?.storageUsedPct ?? null}
                                       total={s.health?.storageTotalBytes ?? null}
                                    />
                                 </Metric>
                                 <Metric>
                                    <MetricLbl>Uptime</MetricLbl>
                                    <CellMain>
                                       {uptimeLabel(s.health?.uptimeSec ?? null)}
                                    </CellMain>
                                 </Metric>
                              </CardMetrics>
                              <CardRows>
                                 <CardRow>
                                    <CardKey>Tunnel IP</CardKey>
                                    <CardVal $mono>{s.vpnIp || '—'}</CardVal>
                                 </CardRow>
                                 <CardRow>
                                    <CardKey>Last seen</CardKey>
                                    <CardVal>{agoLabel(s.handshakeAgoSec)}</CardVal>
                                 </CardRow>
                              </CardRows>
                              <CardActions>
                                 {walledOn[s.id] === false ? (
                                    <PendingChip>Cut-off pending</PendingChip>
                                 ) : null}
                                 <DangerBtn
                                    type="button"
                                    onClick={() => openDelete(s)}
                                 >
                                    Delete
                                 </DangerBtn>
                              </CardActions>
                           </ServerCard>
                        );
                     })}
                  </CardGrid>
               )}
            </>
         )}

         {rows.length > 0 && connected.length === 0 && (
            <EmptyNote>
               Nothing is reporting yet. Finish the pastes above and the routers
               appear here on their own.
            </EmptyNote>
         )}

         {unreadable.length > 0 && (
            <NoteStrip $tone="warn">
               <NoteTitle>
                  SmartFi cannot read{' '}
                  {unreadable.length === 1
                     ? 'one of these routers'
                     : `${unreadable.length} of these routers`}
               </NoteTitle>
               <NoteList>
                  {unreadable.map((s) => (
                     <li key={s.id}>
                        <strong>{s.name}</strong> — {s.health.lastProbeError}
                     </li>
                  ))}
               </NoteList>
               <NoteBody>
                  Their tunnel is up, so the router is there. Pasting part 2 again
                  from the “Setup script” button fixes the usual cause.
               </NoteBody>
            </NoteStrip>
         )}

         {showAccountsGap && (
            <NoteStrip $tone="plain">
               <NoteBody>
                  <strong>Subscribers</strong> counts records in SmartFi, and you
                  have none yet — so it reads 0 while your MikroTik is carrying{' '}
                  {clientTotals.onRouter} PPPoE{' '}
                  {clientTotals.onRouter === 1 ? 'account' : 'accounts'} of its
                  own. Suspended and cut-off are billing states SmartFi keeps, not
                  something the router can tell us. They fill in as you add
                  accounts.
               </NoteBody>
            </NoteStrip>
         )}
      </IspPage>
   );
};

/** Amber, because unlike the others this one needs the operator to act. */
const BlockedNotice = styled.div`
   display: flex;
   gap: 10px;
   align-items: flex-start;
   margin-bottom: 10px;
   padding: 12px 14px;
   border: 1px solid #fcd34d;
   border-left: 5px solid #f59e0b;
   border-radius: 12px;
   background: #fffbeb;
   color: #92400e;
   font-size: 13px;
   line-height: 1.55;
`;
const WallNotice = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 16px;
   flex-wrap: wrap;
   margin-bottom: 14px;
   padding: 14px 16px;
   border: 1px solid ${colors.RED};
   border-radius: 12px;
   background: rgba(220, 53, 69, 0.06);
`;
const WallText = styled.div`
   font-size: 13px;
   line-height: 1.6;
`;
const WallBtn = styled.button`
   font-family: inherit;
   font-size: 14px;
   font-weight: 800;
   padding: 10px 18px;
   border: none;
   border-radius: 9px;
   background: ${colors.PRIMARY};
   color: #fff;
   cursor: pointer;
   white-space: nowrap;
`;

/** Amber chip: SmartFi is still finishing this router off, by itself. */
const PendingChip = styled.span`
   font-size: 11px;
   font-weight: 800;
   padding: 4px 10px;
   border-radius: 999px;
   color: #b26a00;
   background: rgba(245, 158, 11, 0.14);
   white-space: nowrap;
`;

export default IspServers;

/* ─── Header ──────────────────────────────────────────────── */

const HeaderRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
`;

const PageTitle = styled.h1`
   margin: 0;
   font-size: 20px;
`;

/* ─── Summary banner ──────────────────────────────────────── */

const Banner = styled.div`
   display: flex;
   align-items: center;
   gap: 16px;
   flex-wrap: wrap;
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   padding: 16px 18px;

   @media (max-width: 720px) {
      gap: 12px;
      padding: 12px;
   }
`;

const BannerIcon = styled.div`
   width: 42px;
   height: 42px;
   flex: 0 0 auto;
   display: grid;
   place-items: center;
   border-radius: 10px;
   font-size: 20px;
   background: ${colors.PRIMARY}18;
`;

const BannerText = styled.div`
   flex: 1 1 260px;
   min-width: 0;
`;

const BannerLabel = styled.div`
   font-size: 11px;
   font-weight: 700;
   letter-spacing: 0.06em;
   text-transform: uppercase;
   color: ${colors.GRAY5};
`;

const BannerHeadline = styled.div`
   display: flex;
   align-items: baseline;
   gap: 6px;
   margin-top: 2px;
   font-size: 15px;
   color: ${colors.MAIN_DARK};
   strong {
      font-size: 20px;
      font-weight: 700;
   }
`;

const Sep = styled.span`
   color: ${colors.GRAY5};
`;

const OnlineNum = styled.span`
   color: ${colors.SUCCESS};
   font-weight: 600;
`;

const BannerSub = styled.div`
   margin-top: 4px;
   font-size: 12px;
   line-height: 1.5;
   color: ${colors.GRAY5};
`;

/**
 * Six figures in an explicit grid: one row of 6 on a wide screen, 3 × 2 on a
 * phone. A wrapping flex row broke them 3-2-1 on a phone with holes beside
 * the last two (Ray, 2026-10-30: "the UI is bad").
 */
const BannerStats = styled.div`
   display: grid;
   grid-template-columns: repeat(6, minmax(0, 1fr));
   gap: 8px;
   flex: 1 1 100%;

   @media (max-width: 720px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 6px;
   }
`;

/**
 * One boxed figure.
 *
 * 🔴 Ray, 2026-09-03: *"adding box so its not look like some random mess
 * numbers."* Bare figures on a flat band ran together — a reader could not
 * tell where one ended and the next began, and the labels are short enough
 * that spacing alone never separated them.
 */
const Stat = styled.div`
   text-align: center;
   min-width: 0;
   padding: 9px 6px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   background: ${colors.WHITE};
`;

const StatNum = styled.div<{ $tone: 'ok' | 'bad' | 'warn' | 'plain' }>`
   font-size: 20px;
   font-weight: 700;
   line-height: 1.1;
   color: ${({ $tone }) =>
      $tone === 'ok'
         ? colors.SUCCESS
         : $tone === 'bad'
           ? colors.DANGER
           : $tone === 'warn'
             ? colors.ORANGE
             : colors.MAIN_DARK};
`;

const StatLbl = styled.div`
   margin-top: 2px;
   font-size: 11px;
   line-height: 1.25;
   color: ${colors.GRAY5};
`;

/* ─── Toolbar ─────────────────────────────────────────────── */

const SectionRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
`;

const SectionHeader = styled.h2`
   margin: 0;
   font-size: 15px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const HeaderActions = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   flex-wrap: wrap;
`;

const SortSelect = styled.select`
   background: ${colors.WHITE};
   color: ${colors.MAIN_DARK};
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   padding: 6px 10px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
`;

const ViewToggle = styled.div`
   display: flex;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   overflow: hidden;
`;

const ToggleBtn = styled.button<{ $active: boolean }>`
   background: ${({ $active }) => ($active ? colors.PRIMARY : 'transparent')};
   color: ${({ $active }) => ($active ? 'white' : colors.GRAY3)};
   border: none;
   padding: 6px 14px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   transition: all 0.15s;
   &:hover {
      background: ${({ $active }) => ($active ? colors.PRIMARY : colors.GRAY14)};
   }
`;

const ActionBtn = styled.button`
   background: ${colors.PRIMARY};
   color: white;
   border: none;
   border-radius: 8px;
   padding: 8px 14px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   white-space: nowrap;
   &:hover {
      opacity: 0.9;
   }
`;

/* ─── Table ───────────────────────────────────────────────── */

const TableWrapper = styled.div`
   overflow-x: auto;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
`;

const StyledTable = styled.table`
   width: 100%;
   border-collapse: collapse;
   font-size: 13px;
   ${actionColumn}
`;

const Th = styled.th`
   text-align: left;
   padding: 10px 12px;
   font-size: 11px;
   font-weight: 600;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.04em;
   background: ${colors.GRAY14};
   white-space: nowrap;
   &:first-child {
      border-top-left-radius: 10px;
   }
   &:last-child {
      border-top-right-radius: 10px;
   }
`;

const Td = styled.td<{ $mono?: boolean; $bold?: boolean; $muted?: boolean }>`
   padding: 10px 12px;
   color: ${({ $muted }) => ($muted ? colors.GRAY5 : colors.MAIN_DARK)};
   border-top: 1px solid ${colors.GRAY13};
   font-family: ${({ $mono }) => ($mono ? 'monospace' : 'inherit')};
   font-size: ${({ $mono }) => ($mono ? '12px' : '13px')};
   font-weight: ${({ $bold }) => ($bold ? 600 : 400)};
   white-space: nowrap;
`;

/* ─── State pill ──────────────────────────────────────────── */

/**
 * `half` is the half-pasted router: part 1 in, part 2 truncated away.
 *
 * Amber, the same as `waiting`, and never green — nothing is broken, a paste is
 * missing. Green here would be the lie the two-part split exists to prevent.
 */
type PillState = 'online' | 'offline' | 'waiting' | 'half';

const toneOf = (s: PillState) =>
   s === 'online' ? colors.SUCCESS : s === 'offline' ? colors.DANGER : colors.ORANGE;

const StatePill = styled.span<{ $state: PillState }>`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 2px 10px;
   border-radius: 10px;
   font-size: 11px;
   font-weight: 600;
   white-space: nowrap;
   background: ${({ $state }) => `${toneOf($state)}18`};
   color: ${({ $state }) => toneOf($state)};
`;

const Dot = styled.span<{ $state: PillState }>`
   width: 7px;
   height: 7px;
   border-radius: 50%;
   flex: 0 0 auto;
   background: ${({ $state }) => toneOf($state)};
`;

/** Monospace and quiet: reference, not a headline. */
const IdChip = styled.button`
   display: block;
   margin-top: 3px;
   padding: 0;
   border: none;
   background: none;
   font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
   font-size: 10.5px;
   font-weight: 500;
   letter-spacing: -0.01em;
   color: ${colors.GRAY5};
   cursor: pointer;

   &:hover {
      color: ${colors.PRIMARY};
   }
`;
const RadiusChip = styled.span`
   display: inline-block;
   margin-left: 8px;
   padding: 1px 7px;
   border-radius: 4px;
   font-size: 10px;
   font-weight: 700;
   letter-spacing: 0.03em;
   background: ${colors.ORANGE}18;
   color: ${colors.ORANGE};
   cursor: help;
`;

const LinkBtn = styled.button`
   background: transparent;
   color: ${colors.PRIMARY};
   border: 1px solid ${colors.PRIMARY}40;
   border-radius: 8px;
   padding: 5px 12px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   white-space: nowrap;
   &:hover {
      background: ${colors.PRIMARY}10;
   }
`;

/* ─── Cards ───────────────────────────────────────────────── */

const CardGrid = styled.div`
   display: grid;
   /* Explicit column count, never auto-fit: a floor width silently drops to
      three columns on a laptop and nobody can see why. */
   grid-template-columns: repeat(3, minmax(0, 1fr));
   gap: 12px;
   @media (max-width: 1200px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
   }
   @media (max-width: 768px) {
      grid-template-columns: minmax(0, 1fr);
   }
`;

const ServerCard = styled.div<{ $state: PillState }>`
   background: ${colors.WHITE};
   border: 1px solid
      ${({ $state }) =>
         $state === 'online' ? `${colors.SUCCESS}30` : colors.GRAY12};
   border-radius: 12px;
   padding: 14px 16px;
   display: flex;
   flex-direction: column;
   gap: 10px;
`;

const CardTop = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 8px;
`;

const CardName = styled.strong`
   font-size: 14px;
   color: ${colors.MAIN_DARK};
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
`;

const CardModel = styled.div`
   margin-top: -6px;
   font-size: 12px;
   color: ${colors.GRAY5};
`;

const CardRows = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

const CardRow = styled.div`
   display: flex;
   align-items: baseline;
   justify-content: space-between;
   gap: 10px;
   font-size: 12px;
`;

const CardKey = styled.span`
   color: ${colors.GRAY5};
   white-space: nowrap;
`;

const CardVal = styled.span<{ $mono?: boolean }>`
   color: ${colors.MAIN_DARK};
   font-family: ${({ $mono }) => ($mono ? 'monospace' : 'inherit')};
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
`;

const CardActions = styled.div`
   display: flex;
   align-items: center;
   justify-content: flex-end;
   gap: ${ROW_ACTION_GAP};
   margin-top: 2px;
`;

const Warn = styled.p`
   margin: 0;
   font-size: 12px;
   line-height: 1.5;
   color: ${colors.ORANGE};
`;

/* ─── Empty states ────────────────────────────────────────── */

const EmptyNote = styled.p`
   margin: 0;
   padding: 22px;
   text-align: center;
   font-size: 13px;
   color: ${colors.GRAY5};
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
`;

const EmptyState = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 8px;
   padding: 28px 18px;
   text-align: center;
   border: 1px dashed ${colors.GRAY12};
   border-radius: 8px;
`;

const EmptyBig = styled.div`
   font-size: 15px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const EmptySub = styled.p`
   margin: 0 0 6px;
   max-width: 420px;
   font-size: 12px;
   line-height: 1.6;
   color: ${colors.GRAY5};
`;

/* ─── Measured cells ──────────────────────────────────────── */

/**
 * A cell that carries a reading and its context — "42" over "of 50 on router",
 * "18%" over a bar over "1.0 GB".
 *
 * The sub-line is what stops the numbers being ambiguous. "42" alone in a
 * Clients column does not say whether it is sessions up, secrets configured or
 * subscribers billed, and those are three different numbers that an operator
 * mid-migration needs to keep apart.
 */
const CellStack = styled.div`
   display: flex;
   flex-direction: column;
   gap: 3px;
   min-width: 74px;
`;

const CellMain = styled.span<{ $tone?: 'ok' }>`
   font-size: 13px;
   font-weight: 700;
   line-height: 1.1;
   color: ${({ $tone }) => ($tone === 'ok' ? colors.SUCCESS : colors.MAIN_DARK)};
`;

const CellSub = styled.span<{ $tone?: 'warn' }>`
   font-size: 10.5px;
   line-height: 1.2;
   white-space: nowrap;
   color: ${({ $tone }) => ($tone === 'warn' ? colors.ORANGE : colors.GRAY5)};
`;

/**
 * Not measured. Distinct from a zero on purpose — an em dash says nobody got an
 * answer, and a 0 would say the router answered and had nothing to report.
 */
const Dash = styled.span`
   font-size: 13px;
   color: ${colors.GRAY5};
`;

const Meter = styled.div`
   width: 100%;
   min-width: 56px;
   height: 4px;
   border-radius: 3px;
   overflow: hidden;
   background: ${colors.GRAY12};
`;

/**
 * Amber at 75%, red at 90%.
 *
 * Never auto-scaled to the row's own range: an idle router normalised against
 * its own 3% noise draws a full bar and reads as a crisis.
 */
const MeterFill = styled.div<{ $pct: number; $tone: 'ok' | 'warn' | 'bad' }>`
   width: ${({ $pct }) => Math.max(2, Math.min(100, $pct))}%;
   height: 100%;
   border-radius: 3px;
   background: ${({ $tone }) =>
      $tone === 'bad'
         ? colors.DANGER
         : $tone === 'warn'
           ? colors.ORANGE
           : colors.SUCCESS};
`;

const ErrChip = styled.span`
   display: inline-block;
   margin-left: 8px;
   padding: 1px 7px;
   border-radius: 4px;
   font-size: 10px;
   font-weight: 700;
   letter-spacing: 0.03em;
   background: ${colors.DANGER}18;
   color: ${colors.DANGER};
   cursor: help;
`;

const GhostBtn = styled.button`
   background: transparent;
   color: ${colors.MAIN_DARK};
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   padding: 7px 12px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   white-space: nowrap;
   &:hover:not(:disabled) {
      background: ${colors.GRAY14};
   }
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;

/* ─── Card metrics ────────────────────────────────────────── */

const CardMetrics = styled.div`
   display: grid;
   /* Explicit column count, never auto-fit: a floor width silently drops a
      column on a laptop and nobody can see why. */
   grid-template-columns: repeat(3, minmax(0, 1fr));
   gap: 10px 8px;
   padding: 10px 0;
   border-top: 1px solid ${colors.GRAY13};
   border-bottom: 1px solid ${colors.GRAY13};
   @media (max-width: 480px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
   }
`;

const Metric = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
   min-width: 0;
`;

const MetricLbl = styled.span`
   font-size: 10px;
   font-weight: 700;
   letter-spacing: 0.05em;
   text-transform: uppercase;
   color: ${colors.GRAY5};
`;

/* ─── Explanations under the table ────────────────────────── */

/**
 * Why a cell is blank, in words.
 *
 * A router SmartFi cannot read and a router nobody has asked yet draw the same
 * em dash. Only one of them is the operator's to fix, so the difference is
 * spelled out here rather than left to a tooltip they will never hover.
 */
const NoteStrip = styled.div<{ $tone: 'warn' | 'plain' }>`
   border: 1px solid
      ${({ $tone }) => ($tone === 'warn' ? `${colors.ORANGE}55` : colors.GRAY12)};
   background: ${({ $tone }) =>
      $tone === 'warn' ? `${colors.ORANGE}10` : colors.GRAY14};
   border-radius: 10px;
   padding: 12px 14px;
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

const NoteTitle = styled.div`
   font-size: 12.5px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const NoteBody = styled.p`
   margin: 0;
   font-size: 12px;
   line-height: 1.6;
   color: ${colors.GRAY5};
`;

const NoteList = styled.ul`
   margin: 0;
   padding-left: 18px;
   font-size: 12px;
   line-height: 1.7;
   color: ${colors.MAIN_DARK};
   strong {
      font-weight: 700;
   }
`;

/** The count beside a section heading, so the two lists are countable at a glance. */
const CountChip = styled.span<{ $tone: 'ok' | 'warn' }>`
   display: inline-block;
   margin-left: 8px;
   padding: 1px 8px;
   border-radius: 10px;
   font-size: 11px;
   font-weight: 700;
   vertical-align: middle;
   background: ${({ $tone }) =>
      $tone === 'ok' ? `${colors.SUCCESS}18` : `${colors.ORANGE}18`};
   color: ${({ $tone }) => ($tone === 'ok' ? colors.SUCCESS : colors.ORANGE)};
`;

const MutedHint = styled.span`
   font-size: 12px;
   color: ${colors.GRAY5};
`;

/**
 * Delete, as an outline rather than a filled red button.
 *
 * It sits on every row of both tables; a solid red one per row would make a
 * page of healthy servers read as a page of problems. The weight belongs on the
 * confirmation, which is where the irreversible thing actually happens.
 */
const DangerBtn = styled.button`
   background: transparent;
   color: ${colors.DANGER};
   border: 1px solid ${colors.DANGER}40;
   border-radius: 8px;
   padding: 5px 12px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   white-space: nowrap;
   &:hover {
      background: ${colors.DANGER}10;
   }
`;
